// "Send invite", inside a call.
//
// The lobby has this too, but there it always has a number to text - a room
// raised by another app carries one. A call started by hand carries nothing, so
// this has to be able to ask who to send it to, and remember the answer on the
// call's record rather than only in this component.
//
// Sends the same link Copy link copies, through the same endpoint the lobby
// uses. The link itself is built server-side; nothing here chooses where the
// customer is sent.

import { useCallback, useEffect, useState } from 'react';
import { InspectionsStore } from '../inspections/InspectionsStore';
import { formatPhone } from '../inspections/phone';

type Phase = 'idle' | 'asking' | 'sending' | 'sent';

export function InviteButton({ callId }: { callId: string }) {
  const [rowId, setRowId] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [problem, setProblem] = useState<string | null>(null);

  // The record is written when the call is joined, so this usually finds it
  // first time. It is not fatal if it does not: the button asks for a number
  // and the send creates what it needs.
  useEffect(() => {
    let alive = true;
    setRowId(null);
    setPhone('');
    setPhase('idle');
    setProblem(null);
    void InspectionsStore.byCallId(callId).then(({ data }) => {
      if (!alive || !data) return;
      setRowId(data.id);
      setPhone(data.phoneNumber ?? '');
    });
    return () => {
      alive = false;
    };
  }, [callId]);

  const send = useCallback(
    async (number: string) => {
      setPhase('sending');
      setProblem(null);

      // Saved before sending, so the number is on the call's record whether or
      // not the text gets through - and so a second attempt does not ask again.
      if (number && number !== phone) {
        const saved = await InspectionsStore.setPhone(callId, number);
        if (saved.problem) {
          setProblem(saved.problem);
          setPhase('asking');
          return;
        }
        setPhone(number);
      }

      // The row id may not have been known when this mounted - a call joined a
      // moment ago, or one whose record was just created by the save above.
      let id = rowId;
      if (!id) {
        const { data } = await InspectionsStore.byCallId(callId);
        id = data?.id ?? null;
        setRowId(id);
      }
      if (!id) {
        setProblem('This call has no record yet. Try again in a moment.');
        setPhase('idle');
        return;
      }

      const { problem: failed } = await InspectionsStore.invite(id);
      if (failed) {
        setProblem(failed);
        setPhase('idle');
        return;
      }
      setPhase('sent');
      setTimeout(() => setPhase('idle'), 3000);
    },
    [callId, phone, rowId],
  );

  if (phase === 'asking') {
    return (
      <form
        className="invite"
        onSubmit={(e) => {
          e.preventDefault();
          if (typed.trim()) void send(typed.trim());
        }}
      >
        <input
          autoFocus
          type="tel"
          inputMode="tel"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="(555) 123-4567"
          aria-label="Number to text the link to"
        />
        <button type="submit" className="ghostbtn" disabled={!typed.trim()}>
          Send
        </button>
        <button type="button" className="ghostbtn" onClick={() => setPhase('idle')}>
          Cancel
        </button>
        {problem && <span className="invite__problem">{problem}</span>}
      </form>
    );
  }

  return (
    <>
      <button
        type="button"
        className="ghostbtn"
        disabled={phase === 'sending'}
        title={phone ? `Text the link to ${formatPhone(phone)}` : 'Text the link to a number'}
        onClick={() => {
          if (phone) void send(phone);
          else {
            setTyped('');
            setPhase('asking');
          }
        }}
      >
        {phase === 'sending'
          ? 'Sending…'
          : phase === 'sent'
            ? 'Sent'
            : phone
              ? 'Send invite'
              : 'Send invite…'}
      </button>
      {problem && <span className="invite__problem">{problem}</span>}
    </>
  );
}
